/**
 * Shared parser/formatter for the simple-shape JSONata references that the
 * console authors when wiring one node's top-level output field into
 * another node's top-level input field — i.e.
 *
 *     $.<sourceKey>.<field>           // plain identifier source
 *     $.`<sourceKey>`.<field>         // backtick-escaped (for hyphens etc)
 *     $."<sourceKey>".<field>         // legacy form emitted by older
 *                                       "Insert ref…" UI; technically a
 *                                       JSONata string literal, not a
 *                                       field lookup — but we accept it
 *                                       on the way IN so existing
 *                                       workflows open in Connect mode.
 *
 * Anything more complex (array indices, nested paths, operators,
 * functions, filter predicates) is intentionally NOT matched here — those
 * rows fall through to Advanced (raw JSONata textbox) mode, and the
 * runner evaluates them exactly as it always has.
 *
 * The on-disk shape (`inputMapping: Record<string, unknown>` of
 * JSONata-or-literal) is unchanged; this module only handles the
 * console's UI lens over those strings.
 */

/** A parsed top-level reference: source node key + the single top-level field name (or null if the reference is to the whole upstream output object). */
export interface ITopLevelRef {
  /** The upstream node key (or the literal string `"__input"` for the workflow trigger). */
  sourceKey: string;
  /** The top-level field on the source; `null` means "the whole output object". */
  field: string | null;
}

/**
 * Parse a stored mapping value into `{sourceKey, field}` iff it's shaped
 * like a top-level reference. Returns `null` for literals, complex
 * expressions, or anything that isn't a string.
 *
 * Accepts three source-key forms:
 *   - plain identifier:   `$.classify.alarmCode`
 *   - backtick-escaped:   ``$.`procedure-select`.alarmCode``
 *   - double-quoted:      `$."classify".alarmCode`   (legacy, see header)
 *
 * Accepts at most one trailing `.field` segment. Anything with deeper
 * paths (`$.a.b.c`), brackets, parens, or operators is rejected — those
 * are Advanced-mode territory.
 */
export function parseTopLevelRef(value: unknown): ITopLevelRef | null {
  if (typeof value !== 'string') return null;
  // Single regex covers all three source-key escapes, with an optional
  // single-segment trailing field. Restricting the field to a plain
  // identifier keeps "complex JSONata" out of Connect mode.
  const m = /^\s*\$\.(?:`([^`]+)`|"([^"]+)"|([a-zA-Z_$][\w-]*))(?:\.([a-zA-Z_$][\w$]*))?\s*$/.exec(
    value,
  );
  if (!m) return null;
  const sourceKey = m[1] ?? m[2] ?? m[3];
  if (!sourceKey) return null;
  const field = m[4] ?? null;
  return { sourceKey, field };
}

/**
 * Inverse of `parseTopLevelRef`. Produces the canonical persisted form
 * for a Connect-mode row. Uses backticks when the source key contains
 * any character that would break a plain identifier (notably `-`); plain
 * `$.foo.bar` otherwise.
 *
 * Passing `field = null` produces a "whole upstream output" reference
 * (`$.<sourceKey>`) — used when a user picks a source but no specific
 * field (e.g. forwarding an entire upstream payload).
 */
export function formatTopLevelRef(sourceKey: string, field: string | null): string {
  const needsEscape = !/^[a-zA-Z_$][\w$]*$/.test(sourceKey);
  const head = needsEscape ? `\`${sourceKey}\`` : sourceKey;
  return field ? `$.${head}.${field}` : `$.${head}`;
}

/**
 * Convenience: is this stored mapping value renderable in Connect mode
 * with no information loss? True iff `parseTopLevelRef` returns a hit.
 */
export function isTopLevelRef(value: unknown): boolean {
  return parseTopLevelRef(value) !== null;
}
